import { Check, LayoutGrid, LayoutList, MousePointerClick, RotateCcw, Rows2, Rows4, Trash2, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { WIDGET_TONES, type WidgetConfig, type WidgetSize } from '../types'
import { WIDGET_META } from '../widgets/meta'
import { WIDGETS } from '../widgets/registry'
import { TONE_STYLES } from '../widgets/tones'
import { PersonalBadge } from '../widgets/WidgetFrame'

const SIZES: WidgetSize[] = [1, 2, 3, 4]

type Patch = Partial<Omit<WidgetConfig, 'id' | 'type'>>

type Props = {
  widget: WidgetConfig | null
  onChange: (patch: Patch) => void
  onReset: () => void
  onRemove: () => void
  onClose: () => void
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2.5 border-t border-border/60 pt-4">
      <h3 className="text-xs font-semibold">{title}</h3>
      {children}
    </section>
  )
}

function Segmented<V extends string | number>({ label, value, options, onChange }: { label: string; value: V; options: { value: V; label: ReactNode }[]; onChange: (v: V) => void }) {
  return (
    <div role="radiogroup" aria-label={label} className="grid auto-cols-fr grid-flow-col gap-1.5">
      {options.map((o) => {
        const active = o.value === value
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            className={cn(
              'flex h-8 items-center justify-center gap-1.5 rounded-lg border text-xs font-medium transition-colors [&_svg]:size-3.5',
              active ? 'border-primary bg-primary text-primary-foreground shadow-sm' : 'bg-card hover:border-primary/40 hover:bg-muted',
            )}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

export function WidgetProperties({ widget, onChange, onReset, onRemove, onClose }: Props) {
  const { t } = useTranslation()

  if (!widget) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-12 text-center">
        <span className="flex size-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <MousePointerClick className="size-5" />
        </span>
        <p className="text-sm font-medium">{t('dashboard.edit.noSelection')}</p>
        <p className="text-xs text-muted-foreground">{t('dashboard.edit.noSelectionHint')}</p>
      </div>
    )
  }

  const meta = WIDGET_META[widget.type]
  const Icon = WIDGETS[widget.type].icon
  const sizeOptions = SIZES.map((s) => ({ value: s, label: s }))

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between gap-2 px-4 pt-4">
        <h2 className="text-sm font-semibold">{t('dashboard.edit.properties')}</h2>
        <Button variant="ghost" size="icon" className="size-7" aria-label={t('common.close')} onClick={onClose}>
          <X />
        </Button>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto scrollbar-none p-4">
        <div className="flex items-start gap-3">
          <span className={cn('flex size-12 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset', TONE_STYLES[widget.tone].icon)}>
            <Icon className="size-5" aria-hidden />
          </span>
          <div className="min-w-0 space-y-1">
            <p className="truncate text-sm font-semibold">{t(`dashboard.widgets.${widget.type}.title`)}</p>
            <p className="text-xs text-muted-foreground">{t(`dashboard.widgets.${widget.type}.description`)}</p>
            {meta.personal ? <PersonalBadge /> : null}
          </div>
        </div>

        <Section title={t('dashboard.edit.size')}>
          <p className="text-[11px] text-muted-foreground">{t('dashboard.edit.width')}</p>
          <Segmented label={t('dashboard.edit.width')} value={widget.w} options={sizeOptions} onChange={(w) => onChange({ w })} />
          <p className="pt-1 text-[11px] text-muted-foreground">{t('dashboard.edit.height')}</p>
          <Segmented label={t('dashboard.edit.height')} value={widget.h} options={sizeOptions} onChange={(h) => onChange({ h })} />
        </Section>

        {meta.supportsView ? (
          <Section title={t('dashboard.edit.view')}>
            <Segmented
              label={t('dashboard.edit.view')}
              value={widget.view}
              onChange={(view) => onChange({ view })}
              options={[
                { value: 'card', label: <><LayoutGrid />{t('dashboard.edit.viewCard')}</> },
                { value: 'list', label: <><LayoutList />{t('dashboard.edit.viewList')}</> },
              ]}
            />
          </Section>
        ) : null}

        <Section title={t('dashboard.edit.tone')}>
          <div role="radiogroup" aria-label={t('dashboard.edit.tone')} className="flex flex-wrap gap-2.5">
            {WIDGET_TONES.map((tone) => {
              const active = widget.tone === tone
              return (
                <button
                  key={tone}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  aria-label={t(`dashboard.edit.tones.${tone}`)}
                  title={t(`dashboard.edit.tones.${tone}`)}
                  onClick={() => onChange({ tone })}
                  className={cn(
                    'flex size-7 items-center justify-center rounded-full text-primary-foreground ring-offset-2 ring-offset-card transition-shadow',
                    TONE_STYLES[tone].swatch,
                    active ? 'ring-2 ring-foreground/70' : 'hover:ring-2 hover:ring-border',
                  )}
                >
                  {active ? <Check className="size-3.5" strokeWidth={3} /> : null}
                </button>
              )
            })}
          </div>
        </Section>

        <Section title={t('dashboard.edit.density')}>
          <Segmented
            label={t('dashboard.edit.density')}
            value={widget.density}
            onChange={(density) => onChange({ density })}
            options={[
              { value: 'comfortable', label: <><Rows2 />{t('dashboard.edit.comfortable')}</> },
              { value: 'compact', label: <><Rows4 />{t('dashboard.edit.compact')}</> },
            ]}
          />
        </Section>
      </div>

      <div className="grid gap-2 border-t border-border/60 p-4">
        <Button variant="outline" size="sm" onClick={onReset}>
          <RotateCcw />
          {t('dashboard.edit.resetWidget')}
        </Button>
        <Button variant="cancel" size="sm" onClick={onRemove}>
          <Trash2 />
          {t('dashboard.edit.remove')}
        </Button>
      </div>
    </div>
  )
}
